import { ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Viewport-filling page layout: standard gutters, an optional PageTopRow, and
 * a body that takes the remaining height. The bottom padding is one gutter so
 * a ContentCard's bottom edge sits level with the sidebar's.
 *
 *   <PageShell>
 *     <PageTopRow>…back button, tabs, actions…</PageTopRow>
 *     <ContentCard>…the page…</ContentCard>
 *   </PageShell>
 */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('px-gutter pt-page-top pb-gutter h-screen flex flex-col relative', className)}>
      {children}
    </div>
  );
}

/**
 * Fixed-height row above the content: never shrinks, keeps the standard gap
 * and bottom margin. min-h-12 is the 3rem row height that
 * --spacing-page-chrome documents ("the height of the controls in it"), so a
 * page whose top row holds only a title still occupies the same header block
 * as one holding a toggle or search field, and its content card starts at
 * the same y as every other page's. Its first control (back button, tabs)
 * starts on --spacing-page-inset, the line page titles, table checkboxes and
 * ContentCard content all use.
 */
export function PageTopRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-2 mb-header-gap shrink-0 min-h-12 pl-[calc(var(--spacing-page-inset)_-_var(--spacing-gutter))]', className)}>
      {children}
    </div>
  );
}

/**
 * Edge fades for a scrolling region: a short fade at the top once the content
 * has been scrolled, and a taller one at the bottom until the end is reached,
 * both in the colour of the surface behind the content so it dissolves into
 * that surface rather than being cut off. Neither shows when nothing overflows.
 */
function useEdgeFade() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const canScroll = el.scrollHeight > el.clientHeight + 1;
      setFade({
        top: canScroll && el.scrollTop > 1,
        bottom: canScroll && el.scrollTop + el.clientHeight < el.scrollHeight - 1,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    // Content usually grows after mount (data arriving), which changes
    // scrollHeight without resizing the scroller itself, so watch the
    // children's sizes too and re-attach whenever the children change.
    const resize = new ResizeObserver(update);
    const observeChildren = () => {
      resize.disconnect();
      resize.observe(el);
      Array.from(el.children).forEach((child) => resize.observe(child));
    };
    observeChildren();
    const mutation = new MutationObserver(() => {
      observeChildren();
      update();
    });
    mutation.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', update);
      resize.disconnect();
      mutation.disconnect();
    };
  }, []);

  return { scrollRef, fade };
}

// Whole class strings, so Tailwind picks them up.
const FADE_SURFACE = {
  card: 'from-card to-card/0',
  panel: 'from-surface-panel to-surface-panel/0',
  background: 'from-background to-background/0',
} as const;

function EdgeFades({
  fade,
  surface,
  topClassName,
  bottomClassName,
}: {
  fade: { top: boolean; bottom: boolean };
  surface: keyof typeof FADE_SURFACE;
  topClassName?: string;
  bottomClassName?: string;
}) {
  return (
    <>
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-10 bg-gradient-to-b transition-opacity duration-200',
          FADE_SURFACE[surface],
          topClassName,
          fade.top ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t transition-opacity duration-200',
          FADE_SURFACE[surface],
          bottomClassName,
          fade.bottom ? 'opacity-100' : 'opacity-0'
        )}
      />
    </>
  );
}

/**
 * White rounded panel that fills whatever height is left in a PageShell.
 * Long content scrolls inside the card rather than the page; when it does,
 * the edges fade the way the sequence step list and the agent chat do
 * (scrollbar hidden, white fade at the top once scrolled and at the bottom
 * until the end). The radius matches the sidebar panel.
 */
export function ContentCard({
  children,
  className,
  padded = true,
  tinted = false,
  flush = false,
  toolbar = false,
}: {
  children: ReactNode;
  className?: string;
  /** Set false when the content brings its own padding (e.g. an edge-to-edge table). */
  padded?: boolean;
  /** A light grey fill instead of white, for a panel whose content brings its own white surfaces. */
  tinted?: boolean;
  /** Square and edge to edge: runs out through PageShell's gutter to the
      sidebar and the window's right and bottom edges, keeping only a rule
      along its top. */
  flush?: boolean;
  /** The content opens with a toolbar row (a select, search, actions) above a
      table: the row gets header-gap above it, matching the gap beneath it,
      like a page's header bar. */
  toolbar?: boolean;
}) {
  const { scrollRef, fade } = useEdgeFade();

  return (
    <div className={cn(
      'relative flex-1 min-h-0 flex flex-col overflow-hidden',
      flush ? '-mx-gutter -mb-gutter border-t border-panel-border' : 'border border-panel-border rounded-panel',
      tinted ? 'bg-surface-panel' : 'bg-card',
      className,
    )}>
      <EdgeFades fade={fade} surface={tinted ? 'panel' : 'card'} topClassName={flush ? undefined : 'rounded-t-panel'} bottomClassName={flush ? undefined : 'rounded-b-panel'} />
      <div
        ref={scrollRef}
        className={cn(
          'flex-1 min-h-0 flex flex-col overflow-y-auto',
          // Content starts on the page inset from the sidebar's edge, the
          // line page titles and table checkboxes use. A flush card is that
          // far in from its top rule as well.
          // tailwind-merge does not know these spacing tokens, so pick one
          // top padding outright rather than stacking two.
          padded && (flush
            ? [toolbar ? 'pt-header-gap' : 'pt-page-inset', 'px-page-inset pb-card-inset']
            : [toolbar ? 'pt-header-gap' : 'pt-card-inset', 'pb-card-inset px-[calc(var(--spacing-page-inset)_-_var(--spacing-gutter))]']),
        )}
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A scrolling region with ContentCard's edge fades but no card of its own.
 * The fades take the colour of the surface it sits on: the page background
 * by default, or the tinted panel grey (the campaign Sequence step list).
 */
export function PageScrollArea({ children, className, surface = 'background' }: { children: ReactNode; className?: string; surface?: 'background' | 'panel' }) {
  const { scrollRef, fade } = useEdgeFade();

  return (
    <div className={cn('relative flex-1 min-h-0 flex flex-col', className)}>
      <EdgeFades fade={fade} surface={surface} />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        {children}
      </div>
    </div>
  );
}
